import { createReducer, on } from '@ngrx/store';
import { id } from '@wonder-core-ng/interfaces/id.type';
import { IActionExecution } from '../event-store/interfaces/event.interface';
import * as DashboardActions from './dashboard.actions';
import { DateRangeQuery, IDashboard } from './interfaces/dashboard.interface';

export const dashboardFeatureKey = 'dashboard';

export interface State {
  dashboards: IDashboard[];
  selectedDashboardId: id | null;
  httpResult: { action: unknown; error?: unknown };
  editingBlockId: id | null;
  /** The dashboard's date-range filter, or null when the user hasn't set one (no date filtering). */
  queryDateRange: DateRangeQuery | null;
  /** Runtime values of dashboard-wide grouped filters, keyed by source field. */
  globalFilterValues: Record<string, unknown>;
  /** Action executions of each block that carries action lists, keyed by block id. */
  blockActionExecutions: Record<string, IActionExecution[]>;
  blockActionExecutionsLoading: Record<string, boolean>;
  /** The dashboard being opened; a failed load is attributed to it (WB-185). */
  requestedDashboardId: id | null;
  /** Why the last dashboard that was opened could not be read: the HTTP status (0 when there was none). */
  loadError: DashboardLoadError | null;
}

export interface DashboardLoadError {
  dashboardId: id;
  status: number;
}

export const initialState: State = {
  dashboards: [],
  selectedDashboardId: null,
  httpResult: { action: '' },
  editingBlockId: null,
  queryDateRange: null,
  globalFilterValues: {},
  blockActionExecutions: {},
  blockActionExecutionsLoading: {},
  requestedDashboardId: null,
  loadError: null,
};

export const reducer = createReducer(
  initialState,
  on(
    DashboardActions.loadDashboardsSuccess,
    (state, { data }): State => ({
      ...state,
      dashboards: data.list,
    })
  ),
  on(
    DashboardActions.loadDashboard,
    (state, { dashboardId }): State => ({ ...state, requestedDashboardId: dashboardId, loadError: null })
  ),
  on(DashboardActions.loadDashboardFailure, (state, { error }): State => {
    const status = (error as { status?: unknown } | null)?.status;
    return {
      ...state,
      loadError:
        state.requestedDashboardId === null
          ? null
          : { dashboardId: state.requestedDashboardId, status: typeof status === 'number' ? status : 0 },
    };
  }),
  on(DashboardActions.loadDashboardSuccess, (state, { dashboard }): State => {
    if (!dashboard) return state;
    // Opening/reloading a dashboard clears any grouped-filter values from the previous one.
    return {
      ...state,
      dashboards: state.dashboards.some((d) => d.id === dashboard.id)
        ? state.dashboards.map((d) => (d.id === dashboard.id ? dashboard : d))
        : [...state.dashboards, dashboard],
      selectedDashboardId: dashboard.id,
      loadError: null,
      globalFilterValues: {},
      // Executions belong to the blocks of the dashboard being left behind.
      blockActionExecutions: {},
      blockActionExecutionsLoading: {},
    };
  }),
  on(
    DashboardActions.loadBlockActionExecutions,
    (state, { blockId }): State => ({
      ...state,
      blockActionExecutionsLoading: {
        ...state.blockActionExecutionsLoading,
        [`${blockId}`]: true,
      },
    })
  ),
  on(
    DashboardActions.loadBlockActionExecutionsSuccess,
    (state, { blockId, executions }): State => ({
      ...state,
      blockActionExecutions: { ...state.blockActionExecutions, [`${blockId}`]: executions },
      blockActionExecutionsLoading: {
        ...state.blockActionExecutionsLoading,
        [`${blockId}`]: false,
      },
    })
  ),
  // Clearing the flag matters: a card left "loading" would never reach its empty state.
  on(
    DashboardActions.loadBlockActionExecutionsFailure,
    (state, { blockId }): State => ({
      ...state,
      blockActionExecutionsLoading: {
        ...state.blockActionExecutionsLoading,
        [`${blockId}`]: false,
      },
    })
  ),
  on(
    DashboardActions.markActionExecutionReadSuccess,
    (state, { blockId, execution }): State => ({
      ...state,
      blockActionExecutions: {
        ...state.blockActionExecutions,
        [`${blockId}`]: (state.blockActionExecutions[`${blockId}`] ?? []).map((e) =>
          e.id === execution.id ? execution : e
        ),
      },
    })
  ),
  on(
    DashboardActions.createDashboardSuccess,
    (state, { dashboard }): State => ({
      ...state,
      dashboards: [...state.dashboards, dashboard],
    })
  ),
  on(
    DashboardActions.deleteDashboard,
    (state, { dashboardId }): State => ({
      ...state,
      dashboards: state.dashboards.filter((se) => se.id !== dashboardId),
    })
  ),

  on(
    DashboardActions.selectDashboard,
    (state, { dashboardId }): State => ({
      ...state,
      selectedDashboardId: dashboardId,
      globalFilterValues: {},
    })
  ),
  on(
    DashboardActions.updateDashboard,
    (state, { dashboard }): State => ({
      ...state,
      dashboards: state.dashboards.map((dash) =>
        dash.id === dashboard.id ? { ...dashboard } : dash
      ),
    })
  ),
  on(
    DashboardActions.httpResult,
    (state, { action, error }): State => ({
      ...state,
      httpResult: { action, error },
    })
  ),
  on(
    DashboardActions.selectEditingBlockId,
    (state, { id }): State => ({
      ...state,
      editingBlockId: id,
    })
  ),
  on(
    DashboardActions.addChartToBlock,
    (state, { chartId }): State => ({
      ...state,
      dashboards: state.dashboards.map((d) =>
        d.id !== state.selectedDashboardId
          ? d
          : {
              ...d,
              blocks: d.blocks.map((db) =>
                db.id !== state.editingBlockId
                  ? db
                  : { ...db, charts: [{ id: chartId }] as unknown as typeof db.charts }
              ),
            }
      ),
    })
  ),
  on(
    DashboardActions.updateDateRangeQuery,
    (state, { dateRange }): State => ({
      ...state,
      queryDateRange: dateRange,
    })
  ),
  on(
    DashboardActions.setGlobalFilterValue,
    (state, { key, value }): State => ({
      ...state,
      globalFilterValues: { ...state.globalFilterValues, [key]: value },
    })
  )
);
